import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import { useLocale } from '@/shared/i18n/useLocale'

import {
  BreadcrumbContext,
  useBreadcrumbApi,
  type BreadcrumbApi,
  type Crumb,
} from './breadcrumbContext'

export function BreadcrumbProvider({ children }: { children: React.ReactNode }) {
  const [trail, setTrailState] = useState<Crumb[]>([])

  // Stable, so `useBreadcrumbs`'s effect does not re-run on every render of the
  // shell.
  const setTrail = useCallback((next: Crumb[]) => setTrailState(next), [])

  const value = useMemo<BreadcrumbApi>(() => ({ trail, setTrail }), [trail, setTrail])

  return <BreadcrumbContext value={value}>{children}</BreadcrumbContext>
}

/**
 * The trail published by the current screen — see `breadcrumbContext.ts` for why
 * it is not derived from the URL.
 *
 * Renders nothing when a screen has published no trail. A bar containing only
 * "Home" is chrome that says nothing, and it pushed every page down by a row.
 *
 * Directional icons mirror in RTL — §12.3. The chevron between crumbs points
 * along the reading direction, so it must flip; a logo or a photograph would not.
 */
export function Breadcrumbs() {
  const { t } = useTranslation('common')
  const { direction } = useLocale()
  const { trail } = useBreadcrumbApi()

  if (trail.length === 0) return null

  const Separator = direction === 'rtl' ? ChevronLeft : ChevronRight

  return (
    <nav aria-label={t('nav.breadcrumb')} className="w-full px-gutter pt-3 text-sm">
      <ol className="flex flex-wrap items-center gap-1.5">
        <li>
          <Link to="/" className="text-fg-muted hover:text-fg">
            {t('nav.home')}
          </Link>
        </li>
        {trail.map((crumb, index) => (
          // The label is the key: a trail has at most a handful of entries and
          // no two siblings share a label, while the index would make React
          // reuse the wrong node when a screen prepends a crumb.
          <li key={`${crumb.label}-${index}`} className="flex items-center gap-1.5">
            <Separator aria-hidden="true" className="size-3.5 text-fg-subtle" />
            {crumb.to ? (
              <Link to={crumb.to} className="text-fg-muted hover:text-fg">
                {crumb.label}
              </Link>
            ) : (
              // `aria-current` and a heavier weight, because this one is where
              // the user is rather than somewhere they can go.
              <span aria-current="page" className="font-medium">
                {crumb.label}
              </span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  )
}
